import React, { useState } from "react";

import ContractHoldersListItem from "./ListItem.jsx";
import TableWrapper from "../../shared/bootstrap/TableWrapper.jsx";
import TableHeaderColumn from "../../shared/TableHeaderColumn.jsx";

import { filterContractHolders, getOptionName } from "../../../util/contractHoldersList.js";
import { contractHolderSortMap } from "../../../util/sort.js";
import { prepareTextForSearch } from "../../../util/format.js";

import {
  SORT_COMPANY_ASC,
  SORT_COMPANY_DESC,
  SORT_CONTRACT_ASC,
  SORT_CONTRACT_DESC,
  SORT_GROUP_ASC,
  SORT_GROUP_DESC,
  SEARCH_TYPE_NAME,
  NAICS_ELECTRONIC_COMPUTER_MANUFACTURING,
  NAICS_OTHER_COMPUTER_RELATED_SERVICES,
} from "../../../constants.js";

const filterByText = (contractHolders, text, searchOption) => {
  const reg = new RegExp(prepareTextForSearch(text));

  if (searchOption === SEARCH_TYPE_NAME) {
    //only search fullName
    contractHolders = contractHolders.filter(({ fullName }) => reg.test(fullName.toLowerCase()));
  } else {
    //search everything
    contractHolders = contractHolders.filter(
      ({ group, naicsCode, fullName, sewpContractNumber, businessSizes, setAside }) =>
        reg.test(group.toLowerCase()) ||
        reg.test(naicsCode.toLowerCase()) ||
        reg.test(fullName.toLowerCase()) ||
        reg.test(sewpContractNumber.toLowerCase()) ||
        reg.test(businessSizes.toLowerCase()) ||
        reg.test(setAside.toLowerCase())
    );
  }

  return contractHolders;
};

export default function ContractHoldersList({ options, searchType, searchValue, contractHolderListData }) {
  //init state
  const [sortOption, setSortOption] = useState(SORT_COMPANY_ASC);

  //define onClick handlers
  const setCompanySort = () => {
    setSortOption((prevState) => (prevState === SORT_COMPANY_ASC ? SORT_COMPANY_DESC : SORT_COMPANY_ASC));
  };
  const setContractSort = () => {
    setSortOption((prevState) => (prevState === SORT_CONTRACT_ASC ? SORT_CONTRACT_DESC : SORT_CONTRACT_ASC));
  };
  const setGroupSort = () => {
    setSortOption((prevState) => (prevState === SORT_GROUP_ASC ? SORT_GROUP_DESC : SORT_GROUP_ASC));
  };

  //if search input has text, first handle search
  if (searchValue !== "") {
    contractHolderListData = filterByText(contractHolderListData, searchValue, searchType);
  }

  //filter and then sort list
  const filteredContractHolders = filterContractHolders(contractHolderListData, options);

  const groupA = filteredContractHolders.filter(
    ({ naicsCode }) => naicsCode === NAICS_ELECTRONIC_COMPUTER_MANUFACTURING
  );
  const groupBCD = filteredContractHolders.filter(
    ({ naicsCode }) => naicsCode === NAICS_OTHER_COMPUTER_RELATED_SERVICES
  );

  const sortedGroupA = groupA.sort(contractHolderSortMap[sortOption]);
  const sortedGroupBCD = groupBCD.sort(contractHolderSortMap[sortOption]);

  let tableContent = [
    <tbody key="GROUP A" role="rowgroup">
      {
        //only display if there are Group A contract holders
        sortedGroupA.length > 0 && (
          <tr className="groupheader" role="row">
            <th colSpan="3" role="columnheader">
              <strong>Group A:</strong>
            </th>
            <th role="columnheader" className="text-right">
              <strong>NAICS Code: {NAICS_ELECTRONIC_COMPUTER_MANUFACTURING}</strong>
            </th>
          </tr>
        )
      }

      {sortedGroupA.map((item) => (
        <ContractHoldersListItem key={item.sewpContractNumber} contractHolder={item} />
      ))}
    </tbody>,
    <tbody key="GROUP B,C,D" role="rowgroup">
      {
        //only display if there are Group B,C,D contract holders
        sortedGroupBCD.length > 0 && (
          <tr className="groupheader" role="row">
            <th colSpan="3" role="columnheader">
              <strong>Groups B,C,D:</strong>
            </th>
            <th role="columnheader" className="text-right">
              <strong>NAICS Code: {NAICS_OTHER_COMPUTER_RELATED_SERVICES}</strong>
            </th>
          </tr>
        )
      }
      {sortedGroupBCD.map((item) => (
        <ContractHoldersListItem key={item.sewpContractNumber} contractHolder={item} />
      ))}
    </tbody>,
  ];

  // need to show group b,c,d table first when sorted by group asc
  if (sortOption === SORT_GROUP_DESC) {
    tableContent = tableContent.reverse();
  }

  return (
    <TableWrapper
      id="contractholderlist"
      summary={"List of Contract Holders in " + getOptionName(options.groupOptions)}
      caption="Contract Holders sorted by group then company name"
    >
      <thead role="rowgroup">
        <tr role="row">
          <TableHeaderColumn
            id="company-head"
            className="col-xs-6"
            text="Company"
            onClick={setCompanySort}
            title="company name"
            sortOption={sortOption}
            sortDesc={SORT_COMPANY_DESC}
          />

          <TableHeaderColumn
            id="contract-head"
            className="col-xs-2 text-center"
            text="Contract(s)"
            onClick={setContractSort}
            title="contract number"
            sortOption={sortOption}
            sortDesc={SORT_CONTRACT_DESC}
          />

          <TableHeaderColumn
            id="setAside-head"
            className="col-xs-1 text-center"
            text="Group(s)"
            onClick={setGroupSort}
            title="group"
            sortOption={sortOption}
            sortDesc={SORT_GROUP_DESC}
          />

          <TableHeaderColumn id="business-head" className="col-xs-3 text-center" text="Business Designation" />
        </tr>
      </thead>
      {tableContent}
    </TableWrapper>
  );
}
